import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState("checking");
    const [attempts, setAttempts] = useState(0);

    const pollStatus = useCallback(async () => {
        if (!sessionId || attempts >= 10) {
            if (attempts >= 10) setStatus("timeout");
            return;
        }
        try {
            const { data } = await axios.get(`${API}/subscription/status/${sessionId}`, { withCredentials: true });
            if (data.payment_status === "paid") {
                setStatus("success");
                await refreshUser();
                return;
            }
            if (data.status === "expired") {
                setStatus("failed");
                return;
            }
            setAttempts(prev => prev + 1);
            setTimeout(() => pollStatus(), 2000);
        } catch {
            setAttempts(prev => prev + 1);
            setTimeout(() => pollStatus(), 2000);
        }
    }, [sessionId, attempts, refreshUser]);

    useEffect(() => {
        if (sessionId) pollStatus();
        else setStatus("no_session");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="min-h-screen bg-[#0A0A0A]" data-testid="payment-success-page">
            <Navbar />
            <div className="max-w-md mx-auto px-6 py-24 text-center">
                {status === "checking" && (
                    <div className="animate-fadeIn">
                        <Loader2 className="w-12 h-12 text-[#007AFF] animate-spin mx-auto mb-6" />
                        <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Processing Payment
                        </h1>
                        <p className="text-sm text-zinc-400">Verifying your payment status...</p>
                    </div>
                )}

                {status === "success" && (
                    <div className="animate-fadeIn">
                        <CheckCircle className="w-12 h-12 text-[#00C805] mx-auto mb-6" />
                        <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Payment Successful!
                        </h1>
                        <p className="text-sm text-zinc-400 mb-8">Your subscription is now active. Enjoy full access to TradeNalysis.</p>
                        <Button
                            onClick={() => navigate("/dashboard")}
                            className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl px-8 py-5 font-bold"
                            data-testid="go-to-dashboard-btn"
                        >
                            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                )}

                {(status === "failed" || status === "timeout") && (
                    <div className="animate-fadeIn">
                        <XCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-6" />
                        <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            {status === "timeout" ? "Verification Timeout" : "Payment Failed"}
                        </h1>
                        <p className="text-sm text-zinc-400 mb-8">
                            {status === "timeout" ? "We couldn't verify your payment. If you were charged, your subscription will activate shortly." : "The payment did not complete. Please try again."}
                        </p>
                        <Button
                            onClick={() => navigate("/pricing")}
                            className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl px-8 py-5 font-bold"
                            data-testid="retry-payment-btn"
                        >
                            Back to Pricing
                        </Button>
                    </div>
                )}

                {status === "no_session" && (
                    <div className="animate-fadeIn">
                        <p className="text-sm text-zinc-400 mb-4">No payment session found.</p>
                        <Button onClick={() => navigate("/dashboard")} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl px-8 font-bold">
                            Go to Dashboard
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
